
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    MapPin,
    Star,
    Hotel,
    RefreshCw,
    Heart,
    Wifi,
    CarFront,
    Users,
    Waves,
    Coffee,
    Dumbbell,
    Utensils,
    Wind,
} from "lucide-react";

const API_URL = "https://dubaibudgetivehotels.com/api/hotels";
const IMAGE_BASE = "https://uaediscountedhotels.com/uploads/hotels";
const WISHLIST_KEY = "dubai_hotel_wishlist";

const read = (obj, keys) =>
    keys
        .map((key) => obj?.[key])
        .find((value) => value !== undefined && value !== null && value !== "");

const hotelName = (hotel) =>
    read(hotel, ["name", "hotel_name", "title"]) || "Hotel";

const hotelSlug = (hotel) =>
    read(hotel, ["slug", "hotel_slug"]);

function hotelLocation(hotel) {
    const value = read(hotel, [
        "area",
        "location",
        "city",
        "address",
        "destination_name",
    ]);

    if (typeof value === "string") return value;
    if (value && typeof value === "object") {
        return value.name || value.title || "Dubai, UAE";
    }

    return "Dubai, UAE";
}

function imageUrl(hotel) {
    let value = read(hotel, [
        "featured_image",
        "main_image",
        "thumbnail",
        "image",
        "photo",
        "cover_image",
    ]);

    if (!value && Array.isArray(hotel?.images)) {
        value = hotel.images[0];
    }

    if (value && typeof value === "object") {
        value = read(value, [
            "url",
            "image",
            "path",
            "filename",
        ]);
    }

    if (typeof value !== "string" || !value.trim()) {
        return null;
    }

    if (/^https?:\/\//i.test(value)) return value;

    if (value.startsWith("//")) {
        return `https:${value}`;
    }

    return `${IMAGE_BASE}/${value.replace(/^\/+/, "")}`;
}

function getHotels(payload) {
    if (Array.isArray(payload)) return payload;

    const candidates = [
        payload?.data,
        payload?.hotels,
        payload?.results,
        payload?.data?.data,
        payload?.data?.hotels,
    ];

    return candidates.find(Array.isArray) || [];
}

function formatPrice(hotel) {
    const raw = read(hotel, [
        "starting_price",
        "min_price",
        "price_per_night",
        "price",
        "lowest_price",
    ]);

    if (raw == null) return null;

    const number = Number(
        String(raw).replace(/[^0-9.]/g, "")
    );

    if (!Number.isFinite(number) || number <= 0) {
        return null;
    }

    const currency =
        read(hotel, ["currency", "currency_code"]) || "AED";

    return {
        currency,
        amount: number.toLocaleString("en-US", {
            maximumFractionDigits: 0,
        }),
    };
}

function getReviewRating(hotel) {
    const raw = read(hotel, [
        "average_rating",
        "review_rating",
        "guest_rating",
        "rating",
    ]);

    const value = Number(raw);

    return Number.isFinite(value) && value > 0 && value <= 10
        ? value
        : null;
}

function getReviewCount(hotel) {
    const raw = read(hotel, [
        "reviews_count",
        "review_count",
        "total_reviews",
    ]);

    const count = Number(raw);

    return Number.isFinite(count) && count >= 0
        ? count
        : null;
}

const AMENITY_ICONS = {
    wifi: Wifi,
    "free wifi": Wifi,
    "wi-fi": Wifi,
    parking: CarFront,
    "free parking": CarFront,
    pool: Waves,
    "swimming pool": Waves,
    gym: Dumbbell,
    fitness: Dumbbell,
    restaurant: Utensils,
    breakfast: Coffee,
    "air conditioning": Wind,
    "family rooms": Users,
};

function getAmenities(hotel) {
    const raw = read(hotel, [
        "amenities",
        "facilities",
        "hotel_amenities",
    ]);

    if (!raw) return [];

    let items = [];

    if (Array.isArray(raw)) {
        items = raw;
    } else if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            items = Array.isArray(parsed) ? parsed : raw.split(",");
        } catch {
            items = raw.split(",");
        }
    }

    const result = [];
    const seen = new Set();

    items.forEach((item) => {
        const name =
            typeof item === "string"
                ? item
                : item?.name || item?.title || "";

        const normalized = name.toLowerCase().trim();

        const matchedKey = Object.keys(AMENITY_ICONS).find(
            (key) => normalized.includes(key)
        );

        if (!matchedKey) return;

        const Icon = AMENITY_ICONS[matchedKey];

        if (seen.has(matchedKey)) return;

        seen.add(matchedKey);

        result.push({
            name,
            Icon,
        });
    });

    return result.slice(0, 4);
}

function readWishlist() {
    try {
        const saved = JSON.parse(
            localStorage.getItem(WISHLIST_KEY) || "[]"
        );

        return Array.isArray(saved) ? saved : [];
    } catch {
        return [];
    }
}

function WishlistHeart({ hotel }) {
    const id = hotel?.id ?? hotelSlug(hotel);
    const key = id == null ? null : String(id);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (key === null) return;

        const sync = () => {
            setSaved(
                readWishlist().some(
                    (item) => String(item.id) === key
                )
            );
        };

        sync();

        window.addEventListener("hotel-wishlist-updated", sync);
        window.addEventListener("storage", sync);

        return () => {
            window.removeEventListener("hotel-wishlist-updated", sync);
            window.removeEventListener("storage", sync);
        };
    }, [key]);

    if (key === null) return null;

    const toggle = () => {
        const current = readWishlist();

        const exists = current.some(
            (item) => String(item.id) === key
        );

        const next = exists
            ? current.filter((item) => String(item.id) !== key)
            : [
                ...current,
                {
                    id: key,
                    slug: hotelSlug(hotel) || null,
                    name: hotelName(hotel),
                    image: imageUrl(hotel),
                    location: hotelLocation(hotel),
                },
            ];

        try {
            localStorage.setItem(
                WISHLIST_KEY,
                JSON.stringify(next)
            );

            setSaved(!exists);

            window.dispatchEvent(
                new Event("hotel-wishlist-updated")
            );
        } catch {
            // Storage may be unavailable.
        }
    };

    return (
        <button
            type="button"
            onClick={toggle}
            aria-label={
                saved
                    ? `Remove ${hotelName(hotel)} from wishlist`
                    : `Save ${hotelName(hotel)} to wishlist`
            }
            aria-pressed={saved}
            className="
        absolute right-3 top-3 z-20
        flex h-8 w-8 items-center justify-center
        rounded-full bg-[#17324D]/30
        text-white backdrop-blur-sm
        transition hover:scale-110 hover:bg-white/90
        hover:text-[#17324D]
        focus-visible:outline-2
        focus-visible:outline-white
      "
        >
            <Heart
                size={17}
                className={
                    saved
                        ? "fill-[#E84C68] text-[#E84C68]"
                        : ""
                }
            />
        </button>
    );
}

function HotelCard({ hotel, onOpen }) {
    const [imageFailed, setImageFailed] = useState(false);

    const img = imageUrl(hotel);
    const price = formatPrice(hotel);
    const rating = getReviewRating(hotel);
    const reviews = getReviewCount(hotel);
    const amenities = getAmenities(hotel);

    useEffect(() => {
        setImageFailed(false);
    }, [img]);

    return (
        <article
            className="
        group flex h-full min-w-0 flex-col
        overflow-hidden rounded-[13px]
        border border-[#E6ECE8]
        bg-white
        shadow-[0_3px_12px_rgba(0,0,0,0.07)]
        transition-all duration-300
        hover:-translate-y-1
        hover:shadow-[0_12px_30px_rgba(6,78,59,0.13)]
      "
        >
            {/* HOTEL IMAGE */}
            <div
                className="
          relative h-[190px] w-full
          overflow-hidden bg-[#E8F2ED]
          sm:h-[205px] lg:h-[215px]
        "
            >
                <button
                    type="button"
                    onClick={onOpen}
                    aria-label={`View ${hotelName(hotel)}`}
                    className="absolute inset-0 h-full w-full"
                >
                    {img && !imageFailed ? (

                        <img
                            src={img}
                            alt={hotelName(hotel)}
                            loading="lazy"
                            decoding="async"
                            width={400}
                            height={280}
                            sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 25vw"
                            onError={() => setImageFailed(true)}
                            className="
    h-full w-full object-cover
    transition-transform duration-700
    group-hover:scale-105
  "
                        />

                    ) : (
                        <div
                            className="
                flex h-full items-center justify-center
                bg-gradient-to-br
                from-[#DDEFE4] to-[#A9D7BC]
              "
                        >
                            <Hotel
                                size={44}
                                className="text-[#168B43]"
                                strokeWidth={1.3}
                            />
                        </div>
                    )}
                </button>

                <WishlistHeart hotel={hotel} />
            </div>

            {/* CARD CONTENT */}
            <div
                className="
          relative flex flex-1 flex-col
          px-4 pb-4 pt-5
        "
            >
                {/* FLOATING RATING STRIP */}
                {rating !== null && (
                    <div
                        className="
              absolute -top-3 left-3
              flex max-w-[calc(100%-24px)]
              items-center gap-1.5
              rounded-full bg-white
              px-3 py-1.5
              shadow-[0_2px_9px_rgba(0,0,0,0.10)]
            "
                    >
                        <Star
                            size={13}
                            className="fill-[#F5AC24] text-[#F5AC24]"
                        />

                        <span className="text-[11px] font-extrabold text-[#E9A11B]">
                            {rating.toFixed(1)}
                        </span>

                        {reviews !== null && (
                            <span className="truncate text-[10px] text-[#64748B]">
                                ({reviews.toLocaleString()} reviews)
                            </span>
                        )}
                    </div>
                )}

                {/* HOTEL NAME */}
                <button
                    type="button"
                    onClick={onOpen}
                    className="
            line-clamp-2 min-h-[36px]
            text-left text-[15px] font-extrabold
            leading-[1.25] tracking-tight
            text-[#17324D]
            transition hover:text-[#168B43]
          "
                >
                    {hotelName(hotel)}
                </button>

                {/* LOCATION */}
                <p
                    className="
            mt-1 flex items-start gap-1
            text-[11px] text-[#64748B]
          "
                >
                    <MapPin
                        size={13}
                        className="mt-0.5 shrink-0 fill-[#17324D] text-[#17324D]"
                    />
                    <span className="line-clamp-1">
                        {hotelLocation(hotel)}
                    </span>
                </p>

                {/* AMENITY ICONS */}
                <div
                    className="
            mt-4 flex min-h-[28px]
            items-center gap-3
          "
                >

                    {amenities.map(({ name, Icon }, index) => (
                        <span
                            key={`${name}-${index}`}
                            role="img"
                            aria-label={name}
                            title={name}
                            className="
      flex h-7 w-7 items-center justify-center
      rounded-full bg-[#F1F6F3]
      text-[#168B43]
    "
                        >
                            <Icon size={14} aria-hidden="true" />
                        </span>
                    ))}

                </div>

                {/* PRICE */}
                <div className="mt-auto pt-4">
                    {price ? (
                        <>
                            <p className="text-[11px] text-[#7A8A98]">
                                From
                            </p>

                            <p
                                className="
                  mt-0.5 text-[21px] font-extrabold
                  leading-none text-[#168B43]
                "
                            >
                                <span className="text-[15px]">
                                    {price.currency}
                                </span>{" "}
                                {price.amount}
                            </p>

                            <p className="mt-1 text-[10px] text-[#7A8A98]">
                                Indicative rate · confirm dates
                            </p>
                        </>
                    ) : (
                        <p
                            className="
                text-[12px] font-semibold
                text-[#168B43]
              "
                        >
                            Check availability & rates
                        </p>
                    )}
                </div>

                {/* VIEW DETAILS */}
                <button
                    type="button"
                    onClick={onOpen}
                    className="
            mt-3 flex h-10 w-full
            items-center justify-center gap-2
            rounded-[6px] bg-[#168B43]
            text-[12px] font-bold text-white
            shadow-[0_3px_9px_rgba(22,139,67,0.16)]
            transition-all duration-200
            hover:bg-[#087D37]
          "
                >
                    View Details
                    <ArrowRight size={15} />
                </button>
            </div>
        </article>
    );
}

export default function FeaturedHotels({
    limit = 4,
    hotels: suppliedHotels,
}) {
    const navigate = useNavigate();

    const [fetchedHotels, setFetchedHotels] = useState([]);
    const [loading, setLoading] = useState(
        !Array.isArray(suppliedHotels)
    );
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        if (Array.isArray(suppliedHotels)) {
            setLoading(false);
            setError("");
            return;
        }

        const controller = new AbortController();

        async function load() {
            setLoading(true);
            setError("");

            try {
                const response = await fetch(API_URL, {
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(
                        `Request failed (${response.status})`
                    );
                }

                const json = await response.json();

                if (!controller.signal.aborted) {
                    setFetchedHotels(getHotels(json));
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    setError(
                        "Hotels could not be loaded. Please try again."
                    );
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => controller.abort();
    }, [suppliedHotels, retry]);

    const hotels = useMemo(() => {
        const source = Array.isArray(suppliedHotels)
            ? suppliedHotels
            : fetchedHotels;

        const featured = source.filter(
            (hotel) =>
                hotel?.is_featured === true ||
                hotel?.is_featured === 1 ||
                hotel?.is_featured === "1" ||
                hotel?.featured === true ||
                hotel?.featured === 1 ||
                hotel?.featured === "1"
        );

        return (featured.length ? featured : source).slice(
            0,
            Math.max(1, limit)
        );
    }, [suppliedHotels, fetchedHotels, limit]);

    const openHotel = (hotel) => {
        const slug = hotelSlug(hotel);

        if (slug) {
            navigate(`/hotels/${encodeURIComponent(slug)}`);
        } else {
            navigate("/hotels");
        }

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <section
            id="featured-hotels"
            className="bg-white py-12 sm:py-14 lg:py-16"
        >
            {/* SAME WIDTH AS HEADER */}
            <div
                className="
          mx-auto w-full max-w-[1536px]
          px-4 sm:px-6 lg:px-8
        "
            >
                {/* SECTION HEADING */}
                <div
                    className="
            mb-6 flex flex-col justify-between gap-4
            sm:flex-row sm:items-center
          "
                >
                    <div>
                        <p
                            className="
                mb-1 text-[10px] font-extrabold
                uppercase tracking-[0.2em]
                text-[#168B43]
              "
                        >
                            Handpicked For You
                        </p>

                        <h2
                            className="
                text-[27px] font-extrabold
                leading-tight tracking-[-0.04em]
                text-[#17324D]
                sm:text-[32px]
              "
                        >
                            Featured Hotels{" "}
                            <span className="text-[#168B43]">
                                in Dubai
                            </span>
                        </h2>

                        <p
                            className="
                mt-1.5 max-w-[760px]
                text-[12px] leading-5
                text-[#64748B]
                sm:text-[13px]
              "
                        >
                            Discover our most popular and best-value hotels,
                            chosen for comfort, location and great prices.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/hotels")}
                        className="
              group inline-flex h-9 shrink-0
              items-center justify-center gap-2
              self-start rounded-full
              border border-[#9AB9A8]
              bg-white px-4
              text-[11px] font-bold
              text-[#17324D]
              transition-all duration-200
              hover:border-[#168B43]
              hover:bg-[#E8F7EE]
              sm:self-center
            "
                    >
                        View All Hotels

                        <ArrowRight
                            size={14}
                            className="
                transition-transform
                group-hover:translate-x-1
              "
                        />
                    </button>
                </div>

                {/* HOTEL GRID */}
                {loading ? (
                    <div
                        className="
              grid grid-cols-1 gap-4
              sm:grid-cols-2 lg:grid-cols-4
            "
                    >
                        {Array.from(
                            { length: Math.min(Math.max(1, limit), 4) },
                            (_, index) => (
                                <div
                                    key={index}
                                    className="
                    overflow-hidden rounded-[13px]
                    border border-[#E6ECE8]
                  "
                                >
                                    <div className="h-[215px] animate-pulse bg-[#E5F1E9]" />

                                    <div className="space-y-3 p-4">
                                        <div className="h-5 w-3/4 animate-pulse rounded bg-slate-200" />
                                        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
                                        <div className="h-8 animate-pulse rounded bg-slate-100" />
                                        <div className="h-10 animate-pulse rounded bg-[#DCEFE3]" />
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                ) : error ? (
                    <div
                        className="
              rounded-xl border border-[#DCEBE1]
              bg-[#F7FBF8] px-6 py-12 text-center
            "
                    >
                        <p className="text-sm text-[#64748B]">
                            {error}
                        </p>

                        <button
                            type="button"
                            onClick={() => setRetry((value) => value + 1)}
                            className="
                mt-4 inline-flex items-center gap-2
                rounded-lg bg-[#168B43]
                px-5 py-3 text-xs font-semibold text-white
              "
                        >
                            <RefreshCw size={15} />
                            Try Again
                        </button>
                    </div>
                ) : hotels.length > 0 ? (
                    <div
                        className="
              grid grid-cols-1 gap-4
              sm:grid-cols-2 lg:grid-cols-4
            "
                    >
                        {hotels.map((hotel, index) => (
                            <HotelCard
                                key={
                                    hotel.id ??
                                    hotelSlug(hotel) ??
                                    index
                                }
                                hotel={hotel}
                                onOpen={() => openHotel(hotel)}
                            />
                        ))}
                    </div>
                ) : (
                    <div
                        className="
              rounded-xl border border-[#DCEBE1]
              bg-[#F7FBF8] px-6 py-12 text-center
            "
                    >
                        <Hotel
                            size={30}
                            className="mx-auto text-[#168B43]"
                        />

                        <p className="mt-3 text-sm text-[#64748B]">
                            Featured hotels will appear here when available.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
